import { backendClient } from "../api/backend-client";

export class RealtimeChatClient {
  constructor(instructions) {
    if (!instructions) {
      throw new Error('Instructions are required for RealtimeChatClient');
    }

    this.pc = null;
    this.dc = null;
    this._queue = [];
    this.instructions = instructions;

    this.onTextDelta = () => { };
    this.onDone = () => { };
    this.onError = (e) => console.error(e);

    this.sessionUsage = {
      totalTokens: 0,
      inputTokens: 0,
      outputTokens: 0,
      cachedTokens: 0,
      inputTextTokens: 0,
      inputAudioTokens: 0,
      outputTextTokens: 0,
      outputAudioTokens: 0,
      cachedTextTokens: 0,
      cachedAudioTokens: 0,
      responseCount: 0,
      startTime: null,
      endTime: null,
      conversationId: null,
    };
  }

  updateHandlers({ onTextDelta, onDone, onError } = {}) {
    if (onTextDelta) this.onTextDelta = onTextDelta;
    if (onDone) this.onDone = onDone;
    if (onError) this.onError = onError;
  }

  async connect() {
    this.sessionUsage.startTime = Date.now();
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: ["stun:stun.l.google.com:19302"] }],
    });
    const dc = pc.createDataChannel("oai-events");
    this.pc = pc;
    this.dc = dc;

    pc.addTransceiver("audio", { direction: "recvonly" });

    dc.onopen = () => {
      console.log("✅ Data channel opened!");
      this._flushQueue();
    };

    dc.onclose = () => {
      console.log("❌ Data channel closed");
    };

    dc.onerror = (err) => {
      console.error("❌ Data channel error:", err);
    };

    dc.onmessage = (ev) => {
      let msg;
      try {
        msg = JSON.parse(ev.data);
      } catch (e) {
        console.error("Failed to parse message:", e, ev.data);
        return;
      }

      const t = msg.type;

      if (t === "response.output_text.delta") {
        const textChunk = msg.delta ?? msg.text ?? "";
        this.onTextDelta(textChunk);
        return;
      }

      if (t === "response.output_text.done") {
        this.onDone();
        return;
      }

      if (t === "response.done") {
        console.log("[VERBOSE] 📊 Response usage:", msg.response.usage);
        this._accumulateUsage(msg.response.usage);
        return;
      }

      if (t === "error" || msg.error) {
        this.onError(msg.error || msg);
        return;
      }
    };

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    const payload = this.instructions
      ? { sdp: offer.sdp, instructions: this.instructions }
      : offer.sdp;

    const sdpResp = await backendClient.post(
      'realtime/session/chat',
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!sdpResp.data) {
      throw new Error('No SDP answer received from backend');
    }

    const answer = { type: "answer", sdp: sdpResp.data };
    await pc.setRemoteDescription(answer);

    await this._waitForOpen();
  }

  async sendTextWithContext({ contextText, userText }) {
    const text = `### CONTEXT
${contextText}
### QUESTION
${userText}
`;

    this._send({
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "user",
        content: [
          { type: "input_text", text }
        ]
      }
    });

    this._send({ type: "response.create" });
  }

  async sendText(text) {
    this._send({
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "user",
        content: [
          { type: "input_text", text }
        ]
      }
    });

    this._send({ type: "response.create" });
  }

  setConversationMetadata(metadata) {
    this.sessionUsage.conversationId = metadata.conversationId;
  }

  _accumulateUsage(usage) {
    if (!usage) return;

    this.sessionUsage.responseCount += 1;
    this.sessionUsage.totalTokens += usage.total_tokens || 0;
    this.sessionUsage.inputTokens += usage.input_tokens || 0;
    this.sessionUsage.outputTokens += usage.output_tokens || 0;

    // Input token details
    if (usage.input_token_details) {
      this.sessionUsage.inputTextTokens += usage.input_token_details.text_tokens || 0;
      this.sessionUsage.inputAudioTokens += usage.input_token_details.audio_tokens || 0;
      this.sessionUsage.cachedTokens += usage.input_token_details.cached_tokens || 0;

      // Cached token details
      if (usage.input_token_details.cached_tokens_details) {
        this.sessionUsage.cachedTextTokens += usage.input_token_details.cached_tokens_details.text_tokens || 0;
        this.sessionUsage.cachedAudioTokens += usage.input_token_details.cached_tokens_details.audio_tokens || 0;
      }
    }

    // Output token details
    if (usage.output_token_details) {
      this.sessionUsage.outputTextTokens += usage.output_token_details.text_tokens || 0;
      this.sessionUsage.outputAudioTokens += usage.output_token_details.audio_tokens || 0;
    }

    console.log("[VERBOSE] 📊 Accumulated chat session usage:", this.sessionUsage);
  }

  async _sendUsageToBackend() {
    if (this.sessionUsage.responseCount === 0) {
      console.log("[VERBOSE] ℹ️ No usage to report (session had no activity)");
      return;
    }

    try {
      const duration = this.sessionUsage.endTime - this.sessionUsage.startTime;

      const usageData = {
        ...this.sessionUsage,
        sessionDuration: Math.floor(duration / 1000),
      };

      console.log("[VERBOSE] 📤 Sending chat usage to backend:", usageData);

      await backendClient.post('/realtime/usage/chat', usageData);

      console.log("[VERBOSE] ✅ Chat usage data sent successfully");

      // Reset to prevent retry loops
      this.sessionUsage.responseCount = 0;

    } catch (error) {
      console.error("❌ Failed to send chat usage data:", error);
      // Reset to prevent retry loops
      this.sessionUsage.responseCount = 0;
    }
  }


  close() {
    this.sessionUsage.endTime = Date.now();
    this._sendUsageToBackend().finally(() => {
      try { this.dc?.close(); } catch { }
      try { this.pc?.close(); } catch { }
      this.dc = null;
      this.pc = null;
      this._queue = [];
    });
  }


  _send(obj) {
    const payload = JSON.stringify(obj);
    if (this.dc && this.dc.readyState === "open") {
      this.dc.send(payload);
    } else {
      this._queue.push(payload);
    }
  }

  _flushQueue() {
    if (!this.dc || this.dc.readyState !== "open") return;
    for (const msg of this._queue) {
      this.dc.send(msg);
    }
    this._queue = [];
  }

  _waitForOpen() {
    if (this.dc && this.dc.readyState === "open") return Promise.resolve();
    return new Promise((resolve, reject) => {
      let settled = false;
      const timeout = setTimeout(() => {
        if (!settled) {
          settled = true;
          reject(new Error("DataChannel open timeout"));
        }
      }, 10000);

      const check = () => {
        if (this.dc?.readyState === "open") {
          if (!settled) {
            settled = true;
            clearTimeout(timeout);
            resolve();
          }
        } else {
          setTimeout(check, 50);
        }
      };
      check();
    });
  }
}